import React, {useEffect, useMemo, useState} from "react"
import {FormInput} from "./form/FormInput.tsx";
import {Btn} from "./Btn.tsx";
import {OrderFilters, usePaginatedOrders} from "../utils/hooks/usePaginatedOrders.ts";
import {useIntersect} from "../utils/hooks/useIntersect.ts";
import {formatDate, formatPrice, Schema} from "../utils";
import {Loader} from "./Loader.tsx";
import {useCore} from "../hooks";
import {useNavigate, useSearchParams} from "react-router";

export const CheckoutDetailReservationList: React.FC = () => {
  const { selectedDetailCheckoutId, currency } = useCore()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const customerIdFromUrl = useMemo(() => {
    const raw = searchParams.get('customer')
    if (!raw) return undefined
    const n = parseInt(raw, 10)
    return Number.isFinite(n) ? n : undefined
  }, [searchParams])
  const customerNameFromUrl = useMemo(() => {
    const raw = searchParams.get('customerName')
    return raw && raw.length > 0 ? raw : undefined
  }, [searchParams])
  const { measureRef, isIntersecting } = useIntersect()
  const [customer, setCustomer] = useState<string>('')
  const [fulltext, setFulltext] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [orderItem, setOrderItem] = useState<string>('')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [filters, setFilters] = useState<OrderFilters>({
    ...(customerIdFromUrl == null
      ? { checkouts: [Number(selectedDetailCheckoutId)] }
      : {}),
    status: 'reservation',
    ...(customerIdFromUrl != null ? { customer: customerIdFromUrl } : {}),
    customerFulltext: '',
    fulltext: '',
    notes: '',
    item: '',
    dateCreated: {
      left_date: '',
      right_date: '',
    }
  })

  const stripCustomerQueryParams = () => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.delete('customer')
      next.delete('customerName')
      return next
    })
  }

  const applyFilters = (userSubmitted: boolean) => {
    const dropLinkedBecauseFieldSearch =
      userSubmitted && customerIdFromUrl != null && customer.trim() !== ''

    if (dropLinkedBecauseFieldSearch) {
      stripCustomerQueryParams()
    }

    const customerIdForQuery = dropLinkedBecauseFieldSearch ? undefined : customerIdFromUrl
    const restrictToSelectedCheckout = customerIdForQuery == null

    setFilters({
      ...(restrictToSelectedCheckout
        ? { checkouts: [Number(selectedDetailCheckoutId)] }
        : {}),
      status: 'reservation',
      ...(customerIdForQuery != null ? { customer: customerIdForQuery } : {}),
      customerFulltext: customerIdForQuery != null ? '' : customer,
      fulltext,
      notes: note,
      item: orderItem,
      dateCreated: {
        left_date: dateFrom,
        right_date: dateTo,
      }
    })
  }

  const filter = () => applyFilters(true)

  const clearFilters = () => {
    stripCustomerQueryParams()
    setFilters({
      checkouts: [Number(selectedDetailCheckoutId)],
      status: 'reservation',
      customerFulltext: '',
      fulltext: '',
      notes: '',
      item: '',
      dateCreated: {
        left_date: '',
        right_date: '',
      }
    })
    setCustomer('')
    setFulltext('')
    setNote('')
    setOrderItem('')
    setDateFrom('')
    setDateTo('')
  }

  const { refresh, orders, loading, loadMore, isLastPage, totalCount } = usePaginatedOrders({
    filters: {
      ...filters
    },
    sort: 'dateCreated',
    direction: 'DESC',
    schema: Schema.order.list,
  })

  const showReservationDetail = (orderId: number) => {
    navigate(`/checkout-detail/reservation/${orderId}`)
  }

  useEffect(() => {
    if (isIntersecting) {
      loadMore()
    }
  }, [isIntersecting])

  useEffect(() => {
    applyFilters(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDetailCheckoutId, customerIdFromUrl])

  const linkedCustomerTitle =
    customerNameFromUrl && customerNameFromUrl.length > 0
      ? customerNameFromUrl
      : customerIdFromUrl != null
        ? `#${customerIdFromUrl}`
        : ''

  return (
    <div>
      {customerIdFromUrl != null && (
        <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-1">
          <h2 className="m-0 text-lg font-semibold text-black">
            Zákazník:{' '}
            <span className="font-normal">{linkedCustomerTitle}</span>
          </h2>
          <Btn
            className="btn-link btn-error text-xs p-0 h-auto min-h-0 [&_svg]:h-3 [&_svg]:w-3"
            icon={'fa-trash-can'}
            title={'Zrušit filtr zákazníka'}
            children={'Zrušit filtr'}
            onClick={() => stripCustomerQueryParams()}
          />
        </div>
      )}

      <div className="flex flex-row -ml-2 mb-4">
        <div className="w-1/7 px-2">
          <FormInput label={'Zákazník'} value={customer} onChange={val => setCustomer(val)} />
        </div>
        <div className="w-1/7 px-2">
          <FormInput label={'Fulltext'} value={fulltext} onChange={val => setFulltext(val)} />
        </div>
        <div className="w-1/7 px-2">
          <FormInput label={'Poznámka'} value={note} onChange={val => setNote(val)} />
        </div>
        <div className="w-1/7 px-2">
          <FormInput label={'Položka objednávky'} value={orderItem} onChange={val => setOrderItem(val)} />
        </div>
        <div className="w-1/7 px-2">
          <FormInput type={'date'} label={'Datum od'} value={dateFrom} onChange={val => setDateFrom(val)} />
        </div>
        <div className="w-1/7 px-2">
          <FormInput type={'date'} label={'Datum do'} value={dateTo} onChange={val => setDateTo(val)} />
        </div>
        <div className="w-1/7 px-2 flex flex-col">
          <Btn className="btn-link text-red-500 p-0 leading-none h-auto text-xs ml-auto mb-0.5" children={'Smazat filtry'} icon={'fa-filter-circle-xmark'} onClick={clearFilters}/>
          <Btn className="btn-primary" children={'Filtrovat'} icon={'fa-search'} onClick={filter}/>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th className="text-left">ČÍSLO REZERVACE</th>
              <th className="text-left">POKLADNA</th>
              <th className="text-left">DATUM VYSTAVENÍ</th>
              <th className="text-left">JMENO ZÁKAZNÍKA</th>
              <th className="text-left">CENA</th>
              <th className="text-left">VYSTAVIL</th>
              <th className="text-left">POZNÁMKA</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {orders?.length > 0 ? orders.map(order => (
              <tr key={`order-row-${order.id}`}>
                <td className="font-bold">{order.reservationNumber}</td>
                <td>{order.checkout?.name}</td>
                <td>{formatDate(new Date(order.dateCreated), false, true)}</td>
                <td>{order.customer ? `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim() : ''}</td>
                <td>{formatPrice(order.priceAll || 0, currency?.id)}</td>
                <td>{order.createdBy?.email}</td>
                <td>
                  <div className="flex items-center gap-1">
                    <span style={{ maxWidth: '200px'}}>
                      {order.note}
                    </span>
                  </div>
                </td>
                <td className="text-right">
                  <Btn
                    className="btn-link btn text-primary"
                    children={'Zobrazit rezervaci'}
                    icon={'fa-receipt'}
                    onClick={() => showReservationDetail(order.id)}
                  />
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={8} className="text-center py-4">
                  {loading ? <Loader /> : <span className="text-base-400">žádné rezervace</span>}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isLastPage && (
        <div ref={measureRef} className="text-center py-4">
          <span className="text-base-400">next page..</span>
        </div>
      )}
    </div>
  )
}
